// Can the editor open this file, and what body does it open with?
//
// Import-free on purpose, like `lib/task-cancel.ts` and `lib/routine-edit.ts`: it is a rule, it is shared
// 1:1 with iOS and Android, and a jest test can execute it without dragging common-lib's graph in.
//
// The distinction it exists to hold is EMPTY vs ABSENT, which the product got wrong on both sides of the
// wire at once. The file-system used to reject a write carrying no body, so the Files `+` could not create
// a file; and the web client asks `docSource()` — "is there a document to READ here" — which answers no for
// an empty body, so the editor then refused the file the `+` had just written. Both are the same mistake:
// an empty text file is a real file. It is what a file is before anything is typed into it, and what a file
// becomes when its text is cleared.
//
// `docSource` is deliberately NOT changed. The read-only viewer's question is the one it already answers
// correctly — there is nothing to render — and making empty count as a document there would put a blank
// reading surface where an empty state belongs.

export type EditorRefusal = 'reference' | 'too_large';

export interface EditableFile {
  /** Set when the bytes live on WixMP; `content` is then empty by design. */
  referenceUrl?: string;
  /** The stored inline body, if any. */
  content?: string;
}

/**
 * Why the editor may not open this file, or `null` when it may.
 *
 * Only two things genuinely stop it:
 *  - `reference`  the bytes are elsewhere, so there is no inline body to replace. Saving would mean
 *                 re-uploading, and the endpoint refuses it too.
 *  - `too_large`  more than the store will hold inline.
 *
 * Having no body is NOT one of them. Note the order: a reference-backed file reports `reference` even when
 * it is also oversized, because that is the obstacle the user can do something about.
 */
export function editorRefusal(file: EditableFile, tooLarge: boolean): EditorRefusal | null {
  if (isReferenceBacked(file)) return 'reference';
  if (tooLarge) return 'too_large';
  return null;
}

/**
 * The body the editor opens with — `null` meaning "not known yet, keep waiting".
 *
 * `sourceText` is what the caller resolved: the inline body, or the fetched text of a remote one, and
 * `null` when it has resolved neither. `hasRemoteBody` says whether a fetch is the thing that would
 * resolve it.
 *
 * That flag is the whole point of this function. Without it, "no text" is ambiguous between an empty file
 * and a remote file still in flight — and guessing empty there is worse than the bug this fixes: the
 * editor would seed an empty draft, the real text would land too late to replace it, and Save would blank
 * the document.
 */
export function editorSeedBody(sourceText: string | null, hasRemoteBody: boolean): string | null {
  if (sourceText !== null) return sourceText;
  // Nothing fetched and nothing to fetch: an empty inline file, which opens ready to type into.
  return hasRemoteBody ? null : '';
}

/** The bytes live elsewhere. `content` empty is what makes it a pointer rather than a file with both. */
export function isReferenceBacked(file: EditableFile): boolean {
  return !!file.referenceUrl && !file.content;
}
